import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import GroupIcon from "@mui/icons-material/Group";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { styled } from "@mui/material";
import Avatar from "@mui/material/Avatar";
import Badge from "@mui/material/Badge";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Popover from "@mui/material/Popover";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import EntityMember from "_shared/entity-members/entity-member";
import Guid from "_shared/guids/guid";
import Individual from "marketplace/individuals/individual";
import ViewIndividualProfile from "marketplace/individuals/profiles/_views/individual-profile-container";
import { useDeleteForumMutation, useUpdateForumSubscribersMutation } from "messaging/_api/messaging-api";
import { UserAvatarMap } from "messaging/forums/_views/components/forum-chat-window";
import Forum from "messaging/forums/forum";
import { useWhoHasAccess } from "permissions/_views/records/who-has-access";
import { recordPage } from "permissions/_views/wording/permission-wording";
import * as React from "react";
import { useSession } from "users/__session/session-context";
import { RecordAccessLock } from "work/_views/record-access-button";

const SUBSCRIBER_MENU_Z_INDEX = 99999;

const SubscriberMenu = styled(Popover)(() => ({
  zIndex: SUBSCRIBER_MENU_Z_INDEX,
}));
const SubscriberNotificationList = styled(List)(({ theme }) => ({
  maxHeight: theme.spacing(40),
  overflowY: "auto",
  maxWidth: theme.spacing(50),
  minWidth: theme.spacing(30),
}));
const SubscriberButtonSlot = styled("span")(() => ({
  alignContent: "center",
  alignSelf: "stretch",
  borderBottom: "1px solid #aaa",
  display: "flex",
  width: 'min-content'
}));
const SubscriberIcon = styled(IconButton)(() => ({
  alignSelf: "center",
  color: "#666",
  height: "fit-content",
  display: "flex",
}));
const SubscriberMenuTitle = styled(Typography)(({ theme }) => ({
  padding: theme.spacing(1.5, 2, 0.5),
}));
const DisabledActionWrapper = styled("span")(() => ({
  display: "block",
}));

const ADD_SUBSCRIBER_DISABLED_TOOLTIP = "Only the forum creator can add new subscribers.";

type ChatComponentProps = {
  subscribers: Individual[];
  forum: Forum | undefined;
  avatars: UserAvatarMap;
  disableAddButton?: boolean;
  disableLeaveButton?: boolean;
  onAddSubscriber?: () => void;
  onLeaveConversation?: () => void;
};

export default function SubscribersComponent(props: Readonly<ChatComponentProps>) {
  const { subscribers, forum, avatars, disableAddButton: disabledAddButton, disableLeaveButton, onAddSubscriber, onLeaveConversation } = props;

  const [menuAnchor, setMenuAnchor] = React.useState<HTMLElement | null>(null);
  const { openDialog } = useDialog();
  const session = useSession();
  const [deleteForum] = useDeleteForumMutation();
  const [updateForumSubscribers] = useUpdateForumSubscribersMutation();
  const subscriberUpdateOperationId = React.useRef(Guid.generate());
  // The conversation's access is for an administrator with Member Permissions, who need not be in it.
  const whoHasAccess = useWhoHasAccess("forums", "the conversation");
  const forumId = forum?.id;

  function openSubscriberProfile(subscriber: Individual) {
    setMenuAnchor(null);
    openDialog({
      component: <ViewIndividualProfile profile={subscriber.profile} individualId={subscriber.id} />,
      contentSxProps: {
        display: "flex",
      },
      MuiProps: {
        fullWidth: true,
        maxWidth: "lg",
      },
      titleStyle: {
        paddingBottom: 0,
      },
    });
  }

  async function leaveConversation() {
    if (!forum?.id) return;
    if (subscribers.length === 1) {
      await deleteForum({ forumId: forum.id.value }).unwrap();
    } else {
      const newSubscribers = subscribers.filter((subscriber) => subscriber.id !== session?.user?.id);
      const updatedForum = Forum.create({
        ...forum,
        subscribers: newSubscribers.map((individual) =>
          EntityMember.create({
            userId: individual.userId,
            legalEntityId: individual.legalEntityId,
          })
        ),
      });
      await updateForumSubscribers({
        forum: updatedForum,
        operationId: subscriberUpdateOperationId.current,
      }).unwrap();
    }
    onLeaveConversation?.();
  }

  return (
    <React.Fragment>
      <Tooltip title="Member List">
        <SubscriberButtonSlot>
          <SubscriberIcon
            aria-label="open subscriber list"
            disabled={!forum}
            onClick={(event) => setMenuAnchor(menuAnchor ? null : event.currentTarget)}
            size="large"
          >
            <Badge badgeContent={subscribers?.length}>
              <GroupIcon />
            </Badge>
          </SubscriberIcon>
        </SubscriberButtonSlot>
      </Tooltip>
      {/* Who has access in one click, the last icon in the row as it is in a record's header (#6836).
          The member list keeps its own entry for a reader who looks there first. */}
      {whoHasAccess.mayOpen && forumId && (
        <SubscriberButtonSlot>
          <RecordAccessLock button={SubscriberIcon} size="large" onClick={() => whoHasAccess.open(forumId)} />
        </SubscriberButtonSlot>
      )}
      <SubscriberMenu
        anchorEl={menuAnchor}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        <SubscriberMenuTitle variant="subtitle2">Chat Members</SubscriberMenuTitle>
        <SubscriberNotificationList dense>
          {subscribers.map((subscriber: Individual) => (
            <ListItemButton key={subscriber.id.value} onClick={() => openSubscriberProfile(subscriber)}>
              <ListItemAvatar>
                <Avatar src={avatars[subscriber.id.value]} />
              </ListItemAvatar>
              <ListItemText primary={subscriber.fullName} secondary={subscriber.company?.profile?.name} />
            </ListItemButton>
          ))}
        </SubscriberNotificationList>
        <Divider />
        {whoHasAccess.mayOpen && forumId && (
          <List dense>
            <ListItemButton
              onClick={() => {
                setMenuAnchor(null);
                whoHasAccess.open(forumId);
              }}
            >
              <ListItemIcon>
                {/* The lock every record shows for Who has access (#6598). */}
                <LockOutlinedIcon />
              </ListItemIcon>
              <ListItemText>{recordPage.whoHasAccess}</ListItemText>
            </ListItemButton>
          </List>
        )}
        {forum?.creator && (
          <List dense>
            <Tooltip
              title={disabledAddButton ? ADD_SUBSCRIBER_DISABLED_TOOLTIP : ""}
              slotProps={{
                popper: {
                  sx: {
                    zIndex: SUBSCRIBER_MENU_Z_INDEX + 1,
                  },
                },
              }}
            >
              <DisabledActionWrapper>
                <ListItemButton
                  disabled={disabledAddButton}
                  onClick={() => {
                    if (disabledAddButton) return;
                    setMenuAnchor(null);
                    onAddSubscriber?.();
                  }}
                >
                  <ListItemIcon>
                    <GroupAddIcon />
                  </ListItemIcon>
                  <ListItemText>Add People</ListItemText>
                </ListItemButton>
              </DisabledActionWrapper>
            </Tooltip>
            {!disableLeaveButton && (
              <ListItemButton
                onClick={() => {
                  setMenuAnchor(null);
                  void leaveConversation();
                }}
              >
                <ListItemIcon>
                  <ExitToAppIcon />
                </ListItemIcon>
                <ListItemText>Leave Conversation</ListItemText>
              </ListItemButton>
            )}
          </List>
        )}
      </SubscriberMenu>
      {whoHasAccess.drawer}
    </React.Fragment>
  );
}
